import { useContext } from "react";

import { FileData } from "components/TableDetails/components/File/types";
import AuthContext from "context/auth/authContext";
import { getEnvironment } from "shared/functions";
import { FILE_TYPES, TASK_TYPES } from "shared/types/enums";

import { CUSTOM_SECOND_SELECT } from "../CreateTaskContent/SecondSelectContent/types";
import { GROUPS } from "../CreateTaskContent/types";
import { Task } from "../types";

const useTaskTypes = () => {
    const { isAdminUser, isCarrierUser, isSupportUser } =
        useContext(AuthContext);

    const { isProductionEnv } = getEnvironment();

    const tasksNotCreatedViaUi: Task[] = [
        {
            type: TASK_TYPES.RxAutoscan,
            group: GROUPS.General,
            isRetriable: false,
            isRestricted: true
        },
        {
            type: TASK_TYPES.SetBindSim,
            group: GROUPS.General,
            isRetriable: false,
            isRestricted: true
        },
        {
            type: TASK_TYPES.SetForceLockSim,
            group: GROUPS.General,
            isRetriable: false,
            isRestricted: true
        },
        {
            type: TASK_TYPES.SetUnbindSim,
            group: GROUPS.General,
            isRetriable: false,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxAdasFirmware,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxAgpsMt3333,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxAgpsQuectelL89,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxAutoscanCanConfiguration,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxBundleBle,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxBundleBleDtb,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxBundleCan,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxBundleCanDtb,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxBundleDtb,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxBundleFirmware,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxCanAdapterUpdateInternal,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxDsmFirmware,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxFotaCertificate,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxObdChipFirmware,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxObdConfig,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxPSub,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        },
        {
            type: TASK_TYPES.TxGenericFile,
            group: GROUPS.General,
            isRetriable: true,
            isRestricted: true
        }
    ];

    const tasks: Task[] = [
        ...tasksNotCreatedViaUi,

        // Upload firmware
        {
            type: TASK_TYPES.TxFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.Firmware
        },

        // Upload Firmware Bundle Container
        {
            type: TASK_TYPES.TxFirmwareBundle,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.FirmwareBundle
        },

        // Upload configuration
        {
            type: TASK_TYPES.TxConfiguration,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.Configuration
        },

        // Receive configuration
        {
            type: TASK_TYPES.RxConfiguration,
            group: GROUPS.General,
            isRetriable: false
        },

        // Upload 1-Wire authorization list
        {
            type: TASK_TYPES.TxOneWireAuthorizationList,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.OneWireAuthorizationList
        },

        // Receive 1-Wire authorization list
        {
            type: TASK_TYPES.RxOneWireAuthorizationList,
            group: GROUPS.General,
            isRetriable: false
        },

        // Upload Bluetooth authorization list
        {
            type: TASK_TYPES.TxBluetoothAuthorizationList,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.BluetoothAuthorizationList
        },

        // Receive Bluetooth authorization list
        {
            type: TASK_TYPES.RxBluetoothAuthorizationList,
            group: GROUPS.General,
            isRetriable: false
        },

        // Upload manual geofence scenario list
        ...(!isProductionEnv
            ? [
                  {
                      type: TASK_TYPES.TxManualGeofenceList,
                      group: GROUPS.General,
                      isRetriable: true,
                      secondSelect: FILE_TYPES.ManualGeofenceList
                  }
              ]
            : []),

        // Receive manual geofence scenario list
        ...(!isProductionEnv
            ? [
                  {
                      type: TASK_TYPES.RxManualGeofenceList,
                      group: GROUPS.General,
                      isRetriable: false
                  }
              ]
            : []),

        // Set Bluetooth (R) password
        {
            type: TASK_TYPES.SetBluetoothPassword,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: CUSTOM_SECOND_SELECT.SetBluetoothPassword
        },

        // Upload user TLS certificate
        {
            type: TASK_TYPES.TxUserCertificate,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.Certificate
        },

        // Upload BlueNRG firmware
        {
            type: TASK_TYPES.TxBlueNrgFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.BlueNrg
        },

        // Upload Ble firmware
        {
            type: TASK_TYPES.TxBleFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.BleFw
        },

        // Upload GNSS-M Download Assistant
        {
            type: TASK_TYPES.TxGNSSMDownloadAssistant,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.GnssmDownloadAssistant,
            isRestricted: !isAdminUser
        },

        // Upload GNSS-M firmware
        {
            type: TASK_TYPES.TxGNSSMFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.GnssmFw,
            isRestricted: !isAdminUser
        },

        // Upload MeiG Delta Modem Firmware
        {
            type: TASK_TYPES.TxMeigModemDeltaFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.MeiGModemFw,
            isRestricted: !isAdminUser
        },

        // Upload Quectel Delta Modem Firmware
        {
            type: TASK_TYPES.TxQuectelModemDeltaFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.QuectelModemFw,
            isRestricted: !isAdminUser
        },

        // Upload NETPRISMA Delta Modem Firmware
        {
            type: TASK_TYPES.TxNetprismaModemDeltaFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.NetprismaModemFirmware,
            isRestricted: !isAdminUser
        },

        // Update Simcom Delta Modem Firmware
        {
            type: TASK_TYPES.TxSimcomModemDeltaFirmware,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.SimcomModemFirmware,
            isRestricted: !isAdminUser
        },

        // Upload DTB
        {
            type: TASK_TYPES.TxDtb,
            group: GROUPS.General,
            isRetriable: true,
            secondSelect: FILE_TYPES.Dtb,
            isRestricted: !isAdminUser
        },

        // Upload Firmware bundle
        {
            type: TASK_TYPES.TxFirmwareBundleParts,
            group: GROUPS.General,
            isRetriable: false,
            secondSelect: FILE_TYPES.FirmwareBundle,
            isRestricted: !isSupportUser
        },

        // Receive watch-dog (WD) log
        {
            type: TASK_TYPES.RxWdLog,
            group: GROUPS.LogFiles,
            isRetriable: false,
            isRestricted: !isAdminUser
        },

        // Receive internal log
        {
            type: TASK_TYPES.RxLog,
            group: GROUPS.LogFiles,
            isRetriable: false,
            secondSelect: CUSTOM_SECOND_SELECT.Logs
        },

        // Receive black box
        {
            type: TASK_TYPES.RxBlackBox,
            group: GROUPS.LogFiles,
            isRetriable: false,
            isRestricted: !isAdminUser
        },

        // Receive NMEA log
        {
            type: TASK_TYPES.RxNmeaLog,
            group: GROUPS.LogFiles,
            isRetriable: false,
            secondSelect: CUSTOM_SECOND_SELECT.Logs,
            isRestricted: !isSupportUser
        },

        // Receive Exception info
        {
            type: TASK_TYPES.RxExceptionInfo,
            group: GROUPS.LogFiles,
            isRetriable: false,
            isRestricted: !isSupportUser
        },

        // Receive CAN debug log
        {
            type: TASK_TYPES.RxCanDebugLog,
            group: GROUPS.LogFiles,
            isRetriable: false
        },

        // Receive crash dump log
        {
            type: TASK_TYPES.RxCrashDump,
            group: GROUPS.LogFiles,
            isRetriable: false,
            isRestricted: !isSupportUser
        },

        // Upload CAN adapter firmware
        {
            type: TASK_TYPES.TxCanAdapterUpdate,
            group: GROUPS.CanObd,
            isRetriable: true,
            ...(isAdminUser
                ? { secondSelect: CUSTOM_SECOND_SELECT.CanAdapters }
                : {})
        },

        // Change external CAN adapter mode
        {
            type: TASK_TYPES.TxCanAdapterModeChange,
            group: GROUPS.CanObd,
            isRetriable: true,
            secondSelect: CUSTOM_SECOND_SELECT.CanAdapters,
            isRestricted: !isAdminUser
        },

        // Change internal CAN adapter mode
        {
            type: TASK_TYPES.TxCanAdapterModeChangeInternal,
            group: GROUPS.CanObd,
            isRetriable: true,
            secondSelect: CUSTOM_SECOND_SELECT.CanAdapters,
            isRestricted: !isAdminUser
        },

        // Upload FMXY50 CAN chip firmware
        {
            type: TASK_TYPES.TxCanChipFirmware,
            group: GROUPS.CanObd,
            isRetriable: true,
            secondSelect: FILE_TYPES.CanChipFw
        },

        // Upload OEM file
        {
            type: TASK_TYPES.TxCanConfiguration,
            group: GROUPS.CanObd,
            isRetriable: true,
            secondSelect: CUSTOM_SECOND_SELECT.CanVehicles
        },

        // Enable feature
        {
            type: TASK_TYPES.SetEnableFeature,
            group: GROUPS.FeatureControl,
            isRetriable: false,
            secondSelect: CUSTOM_SECOND_SELECT.FeatureControl,
            isRestricted: !isAdminUser
        },

        // Disable feature
        {
            type: TASK_TYPES.SetDisableFeature,
            group: GROUPS.FeatureControl,
            isRetriable: false,
            secondSelect: CUSTOM_SECOND_SELECT.FeatureControl,
            isRestricted: !isAdminUser
        },

        // Update Carrier Apx firmware
        {
            type: TASK_TYPES.TxCarrierApxFirmware,
            group: GROUPS.Carrier,
            isRetriable: true,
            secondSelect: FILE_TYPES.CarrierApxFw,
            isRestricted: !isAdminUser && !isCarrierUser
        },

        // Update Carrier Advance firmware
        {
            type: TASK_TYPES.TxCarrierAdvanceFirmware,
            group: GROUPS.Carrier,
            isRetriable: true,
            secondSelect: FILE_TYPES.CarrierAdvanceFw,
            isRestricted: !isAdminUser && !isCarrierUser
        },

        // Update Carrier Apx settings
        {
            type: TASK_TYPES.TxCarrierApxSettings,
            group: GROUPS.Carrier,
            isRetriable: true,
            secondSelect: FILE_TYPES.CarrierApxSettings,
            isRestricted: !isAdminUser && !isCarrierUser
        },

        // Update Carrier Advance settings
        {
            type: TASK_TYPES.TxCarrierAdvanceSettings,
            group: GROUPS.Carrier,
            isRetriable: true,
            secondSelect: FILE_TYPES.CarrierAdvanceSettings,
            isRestricted: !isAdminUser && !isCarrierUser
        },

        // Download Carrier extractions
        {
            type: TASK_TYPES.RxCarrierExtractions,
            group: GROUPS.Carrier,
            isRetriable: false,
            isRestricted: !isAdminUser && !isCarrierUser
        },

        // Download Carrier temperature logger files
        {
            type: TASK_TYPES.RxCarrierLoggerFiles,
            group: GROUPS.Carrier,
            isRetriable: false,
            isRestricted: !isAdminUser && !isCarrierUser
        }
    ];

    const visibleTasks = tasks.filter(task => !task.isRestricted);

    const isRetriableTaskType = (taskType: TASK_TYPES) =>
        tasks.some(({ type, isRetriable }) => type === taskType && isRetriable);

    const isTaskFileDeleted = (fileId: number | null, file: FileData | null) =>
        fileId !== null && file === null;

    return { tasks, visibleTasks, isRetriableTaskType, isTaskFileDeleted };
};

export { useTaskTypes };
